import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { filter, Subscription, finalize } from "rxjs";
import { environment } from "../../environments/environment";
import { AuthService } from "./auth.service";
import { getOrCreateAuditSessionCorrelationId, rotateAuditSessionCorrelationId } from "./audit-session.storage";
import { centralDateTimeToPlainBackend } from "../utils/central-time.util";

export type AuditEventType =
  | "AUTH_LOGIN_SUCCESS"
  | "AUTH_LOGOUT"
  | "AUTH_TOKEN_EXPIRED"
  | "AUTH_SESSION_END"
  | "NAVIGATE"
  | string;

export interface CreateAuditEventRequest {
  eventType: string;
  route?: string | null;
  label?: string | null;
  correlationId?: string | null;
  createdAtUtc?: string | null;
  metadataJson?: string | null;
}

@Injectable({
  providedIn: "root",
})
export class AuditService implements OnDestroy {
  private readonly API_URL = environment.apiBaseUrl;
  private readonly endpoint = `${this.API_URL}/auditevents/batch`;

  private correlationId = getOrCreateAuditSessionCorrelationId();
  private rotateCorrelationAfterNextFlush = false;
  private queue: CreateAuditEventRequest[] = [];
  private flushTimer: any = null;
  private readonly maxBatchSize = 25;
  private authTerminalSentAtUtcMs: number | null = null;
  private readonly sessionEndSentKey = "auditSessionEndSentForCorrelationId";

  private subs = new Subscription();
  // We intentionally do NOT track every click globally. Use the opt-in AuditClickDirective for important targets.

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private router: Router
  ) {}

  init(): void {
    // If this page load is a browser reload, record it explicitly so admin audit can distinguish
    // refresh boundaries from real tab closes.
    try {
      const navEntry = (performance.getEntriesByType?.("navigation")?.[0] as any) || null;
      const navType = String(navEntry?.type || (performance as any)?.navigation?.type || "").toLowerCase();
      const isReload = navType === "reload" || navType === "1";
      if (isReload) {
        const url = window.location.pathname + window.location.search + window.location.hash;
        this.track("NAVIGATE", { route: url, label: "page_refresh", metadata: { reason: "reload" } }, { flushNow: true });
      }
    } catch {
      // ignore
    }

    // Navigation events
    this.subs.add(
      this.router.events
        .pipe(filter((e) => e instanceof NavigationEnd))
        .subscribe((e) => {
          const url = (e as NavigationEnd).urlAfterRedirects || this.router.url;
          this.track("NAVIGATE", {
            route: url,
            label: "route_change",
            metadata: { url },
          });
        })
    );

    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", (ev: PageTransitionEvent) => {
        if (ev.persisted) return;
        this.sendSessionEndBeacon();
      });
    }

    // No global click capture by design; use opt-in click directive instead.
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  // Login failures are recorded server-side (AuthController) with reason + correlation id.

  trackLogout(): void {
    // This may run right before local auth state is cleared.
    // Important: rotate correlation AFTER flushing this logout event, so the logout stays linked to the session.
    this.rotateCorrelationAfterNextFlush = true;
    this.authTerminalSentAtUtcMs = Date.now();
    this.track("AUTH_LOGOUT", { route: this.router.url, label: "logout" }, { flushNow: true });
  }

  trackTokenExpired(): void {
    // Called while token is still present; flush immediately.
    // Also rotate correlation after flush so next login starts a fresh session.
    this.rotateCorrelationAfterNextFlush = true;
    this.authTerminalSentAtUtcMs = Date.now();
    this.track("AUTH_TOKEN_EXPIRED", { route: this.router.url, label: "token_expired" }, { flushNow: true });
  }

  trackVerifyRespondentResult(input: {
    method: "citation" | "driversLicense";
    caseNumber?: string;
    citationNumber?: string;
    dlLast5?: string;
    importId?: number | null;
    referenceId?: string | null;
    success: boolean;
    message?: string;
  }): void {
    const importId =
      typeof input.importId === "number" && input.importId > 0
        ? input.importId
        : null;
    const referenceId = String(input.referenceId || "").trim() || undefined;
    const entered = {
      method: input.method,
      // Keep what the user entered (cap for safety/DB size).
      caseNumber: (input.caseNumber || "").trim().slice(0, 64),
      citationNumber:
        input.method === "citation"
          ? String(input.citationNumber || "").trim().slice(0, 32)
          : undefined,
      dlLast5: input.method === "driversLicense" ? String(input.dlLast5 || "").trim().slice(0, 16) : undefined,
      importId,
      referenceId,
    };

    this.track("ACTION_VERIFY_RESPONDENT", {
      route: this.router.url,
      label: input.success ? "verify_success" : "verify_failed",
      metadata: {
        input: entered,
        result: { success: input.success },
        ui: { message: (input.message || "").trim().slice(0, 180) || undefined },
      },
    }, { flushNow: true });
  }

  trackEnrollAttempt(input: { courseId?: number; scheduleId?: number; amount?: number }): void {
    this.track("ACTION_ENROLL_ATTEMPT", {
      route: this.router.url,
      label: "enroll_submit",
      metadata: {
        input: {
          courseId: input.courseId ?? null,
          scheduleId: input.scheduleId ?? null,
          amount: input.amount ?? null,
        },
      },
    });
  }

  trackEnrollResult(input: { success: boolean; enrollmentId?: number; message?: string }): void {
    this.track("ACTION_ENROLL_RESULT", {
      route: this.router.url,
      label: input.success ? "enroll_success" : "enroll_failed",
      metadata: {
        result: {
          success: input.success,
          enrollmentId: input.enrollmentId ?? null,
        },
        ui: { message: (input.message || "").trim().slice(0, 220) || undefined },
      },
    }, { flushNow: true });
  }

  trackAction(eventType: string, label: string, metadata: any, flushNow: boolean = false): void {
    this.track(
      eventType as AuditEventType,
      {
        route: this.router.url,
        label,
        metadata,
      },
      { flushNow }
    );
  }

  private track(
    type: AuditEventType,
    data: { route?: string; label?: string; metadata?: any }
    ,
    options?: { flushNow?: boolean }
  ): void {
    if (!this.hasValidAuthForClientEvents() && type !== "ACTION_ACCOUNT_CREATE_SUBMIT") {
      return;
    }

    const event: CreateAuditEventRequest = {
      eventType: type,
      route: data.route ?? this.router.url,
      label: data.label ?? null,
      correlationId: this.correlationId,
      createdAtUtc: centralDateTimeToPlainBackend(),
      metadataJson: data.metadata ? JSON.stringify(data.metadata) : null,
    };

    this.queue.push(event);

    if (options?.flushNow) {
      this.flushSoon(0);
      return;
    }

    this.flushSoon(this.queue.length >= this.maxBatchSize ? 0 : 1500);
  }

  private flushSoon(delayMs: number): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, delayMs);
  }

  private flush(): void {
    if (this.queue.length === 0) return;

    const token = this.auth.getToken();
    if (!token) {
      // If we were waiting to rotate correlation (logout/token-expired) but token is already gone,
      // we still must rotate so the next activity becomes a new session.
      if (this.rotateCorrelationAfterNextFlush) {
        this.rotateCorrelationAfterNextFlush = false;
        this.queue = [];
        this.rotateCorrelationId();
      }
      return;
    }

    const batch = this.queue.splice(0, this.maxBatchSize);

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Correlation-Id": this.correlationId,
    });

    // Fire-and-forget (don’t break UX if it fails)
    this.http
      .post(this.endpoint, { events: batch }, { headers })
      .pipe(
        finalize(() => {
          // Rotate after the flush attempt finishes so headers/events stay consistent.
          if (this.rotateCorrelationAfterNextFlush) {
            this.rotateCorrelationAfterNextFlush = false;
            this.rotateCorrelationId();
          }
        })
      )
      .subscribe({ error: () => {} });
  }

  private hasValidAuthForClientEvents(): boolean {
    const token = this.auth.getToken();
    const expiry = localStorage.getItem("tokenExpiry");
    if (!token || !expiry) return false;

    const expiryDate = new Date(expiry);
    if (Number.isNaN(expiryDate.getTime())) return false;
    return new Date() < expiryDate;
  }

  private rotateCorrelationId(): void {
    this.correlationId = rotateAuditSessionCorrelationId();
    try {
      sessionStorage.removeItem(this.sessionEndSentKey);
    } catch {
      // ignore
    }
  }

  /**
   * Best-effort terminal event when the tab is actually closed (not SPA route changes).
   * Uses fetch keepalive so the request can outlive the page.
   */
  private sendSessionEndBeacon(): void {
    if (!this.hasValidAuthForClientEvents()) return;
    const token = this.auth.getToken();
    if (!token) return;
    // If we already emitted a terminal auth event (logout/token-expired) very recently,
    // don't also send "tab_closed" — it's redundant noise in audit.
    if (this.authTerminalSentAtUtcMs && Date.now() - this.authTerminalSentAtUtcMs < 15_000) return;
    // Only send once per correlation/session (refresh/suspend can trigger pagehide multiple times).
    try {
      const already = sessionStorage.getItem(this.sessionEndSentKey);
      if (already && already === this.correlationId) return;
      sessionStorage.setItem(this.sessionEndSentKey, this.correlationId);
    } catch {
      // ignore (best effort)
    }

    const body = JSON.stringify({
      events: [
        {
          eventType: "AUTH_SESSION_END",
          route: this.router.url,
          label: "pagehide",
          correlationId: this.correlationId,
          createdAtUtc: centralDateTimeToPlainBackend(),
          metadataJson: JSON.stringify({ reason: "pagehide" }),
        },
      ],
    });

    try {
      void fetch(this.endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Correlation-Id": this.correlationId,
        },
        body,
        keepalive: true,
      });
    } catch {
      // ignore
    }
  }
}

